import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/shared/session'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Button, Input } from '@/shared/ui'

interface PasswordRecoveryDialogProps {
  /**
   * The reset token out of the mailed link.
   *
   * Supabase delivered a magic link that signed the person in and fired a
   * `PASSWORD_RECOVERY` event, so this dialog only needed to know whether to be
   * open. PocketBase mails a token instead, which means the route is what opens
   * this and the token is what authorises the change. Nobody is signed in while
   * it happens.
   */
  token: string
}

export default function PasswordRecoveryDialog({ token }: PasswordRecoveryDialogProps) {
  const confirmPasswordReset = useAuthStore((s) => s.confirmPasswordReset)
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (loading) { setErrorMsg('Still saving…'); return }
    if (!password || !confirmPassword) { setErrorMsg('Fill in both password fields.'); return }
    if (password !== confirmPassword) { setErrorMsg('Passwords do not match'); return }
    if (password.length < 8) { setErrorMsg('Password must be at least 8 characters'); return }
    setLoading(true)
    setErrorMsg(null)
    const error = await confirmPasswordReset(token, password)
    setLoading(false)
    if (error) { setErrorMsg(error); return }
    // Spending the token does not sign anybody in, so this ends at the sign-in
    // form rather than inside the app.
    setDone(true)
  }

  return (
    <Dialog open>
      <DialogContent>
        <DialogHeader showCloseButton={false}>
          <DialogTitle>{done ? 'Password changed' : 'Set new password'}</DialogTitle>
          <DialogDescription>
            {done
              ? 'You can sign in with your new password now.'
              : 'Choose a new password for your account.'}
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <DialogFooter>
            <Button type="button" onClick={() => void navigate('/')}>Continue</Button>
          </DialogFooter>
        ) : (
          <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="rp-password" className="text-xs font-medium">New password</label>
              <Input id="rp-password" type="password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                required autoFocus disabled={loading} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="rp-confirm" className="text-xs font-medium">Confirm password</label>
              <Input id="rp-confirm" type="password"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                required disabled={loading} />
            </div>
            {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}
            <DialogFooter>
              <Button type="submit">
                {loading ? 'Saving…' : 'Set password'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
